<script setup lang="ts">
// The shared body of the header menu — language, appearance and the two
// external links. Rendered into BOTH menus: the signed-in <e-account-menu> and
// the signed-out settings dropdown, which differ only in their trigger and
// their last item (Log out / Log in).
//
// One partial rather than two copies, mirroring the IdP's
// `_AccountMenuActions.cshtml`, which this is otherwise a direct port of —
// same values, same icons, same order, so the two products stay legible as one
// design.
//
// Emits nothing: every item reports through the root menu's own select event,
// which the parent already handles.
//
// Every label is wrapped in a <span>, and that is load-bearing. These items are
// LIGHT-DOM elements: each one builds an <e-listbox-item> and moves its authored
// children into it, so the host's only child is that element. A bare
// interpolation compiles to a textContent assignment on the host, which wipes it
// — after a locale switch the row lost its icons and its chevron, because the
// element Vue overwrote was the row itself. With a span, Vue patches the span
// (which travelled along into the listbox-item) and the row survives.
defineProps<{
  locale: string;
  languages?: { value: string; label: string }[];
  /** The persisted tri-state, so the check marks what was PINNED, not what is resolved. */
  mode: string;
  labels: Record<string, string>;
  /** Drop the Appearance submenu — a whitelabel flow owns its single theme. */
  hideAppearance?: boolean;
}>();

// Three modes since `setMode` landed in ui-core 0.2.0-alpha-20260929152837;
// before that `auto` was unreachable and `toggle()` snapped back to it.
const APPEARANCE_MODES = [
  { value: "light", icon: "sun" },
  { value: "dark", icon: "moon" },
  { value: "auto", icon: "screen" },
] as const;
</script>

<template>
  <e-dropdown-menu-sub v-if="languages && languages.length > 1">
    <e-dropdown-menu-sub-trigger leading-icon="translate">
      <span>{{ labels.language }}</span>
    </e-dropdown-menu-sub-trigger>
    <e-dropdown-menu-sub-content placement="left-start">
      <e-dropdown-menu-item
        v-for="lang in languages"
        :key="lang.value"
        :value="`lang:${lang.value}`"
      >
        <span>{{ lang.label }}</span>
        <e-icon v-if="lang.value === locale" slot="trailing" name="check" />
      </e-dropdown-menu-item>
    </e-dropdown-menu-sub-content>
  </e-dropdown-menu-sub>

  <e-dropdown-menu-sub v-if="!hideAppearance">
    <e-dropdown-menu-sub-trigger leading-icon="moon">
      <span>{{ labels.appearance }}</span>
    </e-dropdown-menu-sub-trigger>
    <e-dropdown-menu-sub-content placement="left-start">
      <e-dropdown-menu-item
        v-for="option in APPEARANCE_MODES"
        :key="option.value"
        :value="`appearance:${option.value}`"
        :leading-icon="option.icon"
      >
        <span>{{ labels[option.value] }}</span>
        <e-icon v-if="mode === option.value" slot="trailing" name="check" />
      </e-dropdown-menu-item>
    </e-dropdown-menu-sub-content>
  </e-dropdown-menu-sub>

  <e-dropdown-menu-separator />

  <e-dropdown-menu-item
    value="support"
    leading-icon="support"
    trailing-icon="open-in-new"
  >
    <span>{{ labels.support }}</span>
  </e-dropdown-menu-item>
  <e-dropdown-menu-item
    value="report-issue"
    leading-icon="warning"
    trailing-icon="open-in-new"
  >
    <span>{{ labels.report }}</span>
  </e-dropdown-menu-item>
</template>
